// `useConversationPreviews` — lazy last-message previews for the
// ConversationList rows (fn-49.4, R5).
//
// Every preview call proxies MA, so the cache IS the cost control:
// entries are keyed `(sessionId, last_updated_at)` — idle polls don't
// change `last_updated_at`, so N poll cycles with unchanged versions
// cause ZERO refetches (the load-bearing test). A bumped version
// refetches that row only.
//
// Fetches go through ONE shared queue (concurrency-capped) covering
// both list sections — the hook is called once with ALL rendered rows
// (the list caps at 50, fully rendered — render-triggered fetch; an
// IntersectionObserver earns its keep only with virtualization).
//
// Silent-by-design: a tool/status-only page, an extraction miss, or a
// fetch rejection all cache `null` for that version (NO second fetch,
// no row-level error UI) — the row keeps its status-derived line.
// Retry happens only when the version changes.
//
// The cache resets SYNCHRONOUSLY on adapter identity change (.flow
// memory: context-derived-caches-must-reset-on — the cache key is
// really the adapter; a provider/account swap must never leak the
// previous adapter's message text). Same render-phase reset pattern
// as `useConversations`. Settles are epoch-guarded so late responses
// from a previous adapter — or after unmount — are dropped.
import { useEffect, useRef, useState } from 'react';
import { usePlatformAdapter } from '../context.js';
import { extractText, isBootstrapContent, normalizeWireEvent } from '../stream/reduce-stream.js';
/** Page size for the newest-first preview fetch. */
export const PREVIEW_EVENTS_LIMIT = 10;
/** Max preview fetches in flight at once (shared across all rows). */
export const PREVIEW_CONCURRENCY = 4;
/**
 * Preview text cap — the line is visually clamped anyway, but the
 * preview also joins the row's accessible name (aria-label), which
 * must stay reasonably short.
 */
export const PREVIEW_MAX_LENGTH = 140;
const PREVIEW_MESSAGE_TYPES = new Set(['user.message', 'agent.message']);
/**
 * Walk a newest-first events page for the most recent non-bootstrap
 * `user.message`/`agent.message` with extractable text. Every envelope
 * is normalized via `normalizeWireEvent` FIRST — the console adapter
 * returns nested `/events` envelopes (payload under `.data`) while
 * live-ish shapes are flat; reading `.type`/`.data` raw misses one of
 * them. Returns null when the page has no usable message (the silent
 * status-line fallback). Exported for direct unit testing.
 */
export function extractPreviewText(envelopes) {
    for (const envelope of envelopes) {
        const event = normalizeWireEvent(envelope);
        if (event === null)
            continue;
        if (!PREVIEW_MESSAGE_TYPES.has(event.type))
            continue;
        const raw = extractText(event.data);
        if (raw.length === 0)
            continue;
        if (isBootstrapContent(raw))
            continue;
        // PLAIN TEXT always — never markdown-rendered; whitespace runs
        // collapse so the one-line clamp (and the aria-label) read clean.
        const text = raw.replace(/\s+/g, ' ').trim();
        if (text.length === 0)
            continue;
        return text.length > PREVIEW_MAX_LENGTH ? `${text.slice(0, PREVIEW_MAX_LENGTH)}…` : text;
    }
    return null;
}
const EMPTY_PREVIEWS = new Map();
/**
 * Preview text per session id for the CURRENT row set. Sessions with
 * no preview (pending fetch, tool-only page, extraction miss, fetch
 * failure) are simply absent — callers fall back to the status line.
 */
export function useConversationPreviews(rows) {
    const adapter = usePlatformAdapter();
    const [previews, setPreviews] = useState(EMPTY_PREVIEWS);
    const cacheRef = useRef(new Map());
    /** sessionId → version currently being fetched (per-session serial). */
    const inFlightRef = useRef(new Map());
    const queueRef = useRef([]);
    const activeRef = useRef(0);
    // Epoch — invalidates settles across adapter swaps and unmount ONLY
    // (rows changes keep in-flight fetches valid: same adapter, and the
    // version cache decides relevance on settle).
    const epochRef = useRef(0);
    const rowsRef = useRef(rows);
    rowsRef.current = rows;
    // Adapter identity transition — synchronous render-phase reset, the
    // previous adapter's previews must NEVER survive a swap.
    const adapterRef = useRef(adapter);
    if (adapterRef.current !== adapter) {
        adapterRef.current = adapter;
        cacheRef.current = new Map();
        inFlightRef.current = new Map();
        queueRef.current = [];
        activeRef.current = 0;
        epochRef.current += 1;
        setPreviews(EMPTY_PREVIEWS);
    }
    // `sync` re-binds each render (fresh adapter closure) but is invoked
    // through the ref so settle handlers from earlier renders always run
    // the CURRENT logic against the CURRENT rows.
    const syncRef = useRef(() => undefined);
    syncRef.current = () => {
        const cache = cacheRef.current;
        const inFlight = inFlightRef.current;
        const current = rowsRef.current;
        const currentIds = new Set(current.map((r) => r.sessionId));
        // Prune cache entries not in the current row set (bounded by the
        // 50-row list cap).
        for (const key of Array.from(cache.keys())) {
            if (!currentIds.has(key))
                cache.delete(key);
        }
        // Drop queued jobs that no longer match a current row/version.
        queueRef.current = queueRef.current.filter((q) => current.some((r) => r.sessionId === q.sessionId && r.version === q.version));
        // Enqueue rows whose (sessionId, version) is neither cached nor
        // already in flight nor queued. A session with an OLD version in
        // flight is NOT double-fetched — its settle re-syncs and the
        // version mismatch enqueues the fresh fetch then.
        for (const row of current) {
            const cached = cache.get(row.sessionId);
            if (cached !== undefined && cached.version === row.version)
                continue;
            if (inFlight.has(row.sessionId))
                continue;
            if (queueRef.current.some((q) => q.sessionId === row.sessionId))
                continue;
            queueRef.current.push({ sessionId: row.sessionId, version: row.version });
        }
        // Pump the shared queue up to the concurrency cap.
        while (activeRef.current < PREVIEW_CONCURRENCY && queueRef.current.length > 0) {
            const job = queueRef.current.shift();
            const epoch = epochRef.current;
            const boundAdapter = adapterRef.current;
            activeRef.current += 1;
            inFlight.set(job.sessionId, job.version);
            Promise.resolve()
                .then(() => boundAdapter.getSessionEvents({
                sessionId: job.sessionId,
                order: 'desc',
                limit: PREVIEW_EVENTS_LIMIT,
            }))
                .then((page) => extractPreviewText(page.data), 
            // Fetch rejection → null for THIS version: silent fallback,
            // retried only when the version changes.
            () => null)
                .then((text) => {
                // Unmount/adapter-swap guard: a stale epoch's bookkeeping
                // belongs to a discarded cache — drop it entirely.
                if (epochRef.current !== epoch)
                    return;
                activeRef.current -= 1;
                inFlightRef.current.delete(job.sessionId);
                cacheRef.current.set(job.sessionId, { version: job.version, text });
                syncRef.current();
            });
        }
        // Publish (only on actual change — poll re-renders with an
        // unchanged cache keep the same map identity). Only entries whose
        // cached version matches the CURRENT row's version are published —
        // a bumped row must never show the previous version's text while
        // its refetch is in flight (the cache contract is the PAIR
        // (sessionId, last_updated_at), not the session alone).
        const versionById = new Map(current.map((r) => [r.sessionId, r.version]));
        const next = new Map();
        for (const [id, entry] of cache) {
            if (entry.text === null)
                continue;
            if (versionById.get(id) !== entry.version)
                continue;
            next.set(id, entry.text);
        }
        setPreviews((prev) => {
            if (prev.size === next.size) {
                let same = true;
                for (const [k, v] of next) {
                    if (prev.get(k) !== v) {
                        same = false;
                        break;
                    }
                }
                if (same)
                    return prev;
            }
            return next;
        });
    };
    useEffect(() => {
        // `adapter`/`rows` are consumed through refs (settle paths need
        // the CURRENT values), but they ARE the re-sync triggers: a poll
        // tick delivers fresh rows, a swap delivers a fresh adapter. Same
        // void-marking pattern as useConversations' refreshNonce.
        void adapter;
        void rows;
        syncRef.current();
    }, [adapter, rows]);
    // Unmount guard — invalidate every outstanding settle.
    useEffect(() => {
        return () => {
            epochRef.current += 1;
        };
    }, []);
    return previews;
}
//# sourceMappingURL=use-conversation-previews.js.map